import { useCallback } from "react";
import { toast } from "react-hot-toast";
import { useHeadlinesCRUD } from "@/shared/api/newsquawk-headlines";
import type { HeadlineForm, checkableTags } from "../models/HeadlineForm";

type CheckableTag = (typeof checkableTags)[number];

/**
 * Transforms checkable tag checkboxes to Tag array format
 */
function getCheckedTags(
  tagCheckboxes: HeadlineForm["tagCheckboxes"],
  checkableTags: readonly CheckableTag[]
): Array<{ tag_id: number; name: string; score: number }> {
  if (!tagCheckboxes) return [];

  return checkableTags
    .filter((checkbox) => tagCheckboxes[checkbox.label])
    .map((checkbox) => ({
      tag_id: checkbox.id,
      name: checkbox.label,
      score: 1,
    }));
}

interface UseHeadlineSubmitOptions {
  isEditing: boolean;
  headlineId?: number;
  onSuccess?: () => void;
}

/**
 * Custom hook for handling headline form submission.
 * Encapsulates the transformation logic and mutation calls.
 */
export function useHeadlineSubmit(options: UseHeadlineSubmitOptions) {
  const { isEditing, headlineId, onSuccess } = options;
  const { createMutation, updateMutation } = useHeadlinesCRUD();

  const submitHeadline = useCallback(
    async (data: HeadlineForm, checkableTags: readonly CheckableTag[]) => {
      // Get tags from checkable tag checkboxes (Important, Flash, etc.)
      const checkedTags = getCheckedTags(data.tagCheckboxes, checkableTags);

      // Combine all tags: form tags + checked tags
      const allTags = [...(data.tags ?? []), ...checkedTags];

      const tags = allTags.map((tag) => ({
        tag_id: tag.tag_id,
        score: tag.score,
      }));

      // Build payload (spread data to include all checkbox fields).
      // `tags` is intentionally the lean wire shape {tag_id,score}[] the API
      // accepts; Headline.tags (the read model) is Tag[] ({tag_id,name,score}).
      // Cast is type-only — the payload sent is byte-identical. Do NOT add
      // `name` here (would change the request). See TS-cleanup plan #8.
      const payload = {
        ...data,
        tags: tags as unknown as HeadlineForm["tags"],
      };

      try {
        if (isEditing && headlineId) {
          await updateMutation.mutateAsync({ id: headlineId, ...payload });
          toast.success("Headline updated successfully.");
        } else {
          await createMutation.mutateAsync(payload);
          toast.success("Headline published successfully.");
        }

        onSuccess?.();
      } catch (error) {
        if (isEditing) {
          toast.error(
            "There was an error updating the headline, please try again."
          );
        } else {
          toast.error(
            "There was an error publishing the headline, please try again."
          );
        }
        throw error;
      }
    },
    [isEditing, headlineId, createMutation, updateMutation, onSuccess]
  );

  return {
    submitHeadline,
    isLoading: createMutation.isPending || updateMutation.isPending,
    isError: createMutation.isError || updateMutation.isError,
  };
}
